import React, { useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function SupportImportPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [requesterEmail, setRequesterEmail] = useState(user?.email || "");
  const [collectorEmail, setCollectorEmail] = useState("");
  const [scope, setScope] = useState("standard");
  const [hours, setHours] = useState(2);
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const estPrice = scope === "standard" ? 100 : (Number(hours) || 1) * 100;
  const locale = i18n.resolvedLanguage || i18n.language || "fr";
  const estimatedAmount = useMemo(
    () => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(estPrice),
    [estPrice, locale]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");

    try {
      if (!requesterEmail || !collectorEmail) {
        setError(t("supportImportPage.errors.emailsRequired"));
        setBusy(false);
        return;
      }
      if (scope === "extended" && (!hours || Number(hours) < 1)) {
        setError(t("supportImportPage.errors.hoursRequired"));
        setBusy(false);
        return;
      }

      let filePath = null;
      if (file) {
        const path = `support/${crypto.randomUUID()}/${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from("support_imports")
          .upload(path, file, { upsert: true });
        if (uploadError) throw uploadError;
        filePath = path;
      }

      const { data: inserted, error: insertError } = await supabase
        .from("support_imports")
        .insert({
          user_id: user?.id || null,
          requester_email: requesterEmail,
          collector_email: collectorEmail,
          scope,
          hours_est: scope === "extended" ? Number(hours) : null,
          notes,
          file_path: filePath,
        })
        .select("id")
        .single();
      if (insertError) throw insertError;

      const { data, error: functionError } = await supabase.functions.invoke("create_import_checkout", {
        body: { support_import_id: inserted.id },
      });
      if (functionError) throw functionError;
      if (!data?.sessionId) throw new Error(t("supportImportPage.errors.checkoutNotCreated"));

      const stripe = await stripePromise;
      const { error: redirectError } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
      if (redirectError) throw redirectError;
    } catch (submitError) {
      console.error(submitError);
      setError(submitError.message || t("supportImportPage.errors.generic"));
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-serif mb-2">{t("supportImportPage.title")}</h1>
      <p className="text-gray-600 mb-6">
        {t("supportImportPage.introStandard", { price: "100 €" })}{" "}
        {t("supportImportPage.introExtended", { hourlyPrice: "100 €" })}
      </p>

      <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">{t("supportImportPage.requesterEmail")}</label>
            <input className="w-full border rounded px-3 py-2" value={requesterEmail} onChange={(e) => setRequesterEmail(e.target.value)} type="email" required />
          </div>
          <div>
            <label className="block text-sm mb-1">{t("supportImportPage.collectorEmail")}</label>
            <input className="w-full border rounded px-3 py-2" value={collectorEmail} onChange={(e) => setCollectorEmail(e.target.value)} type="email" required />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1">{t("supportImportPage.importType")}</label>
            <select className="w-full border rounded px-3 py-2" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="standard">{t("supportImportPage.scope.standard", { price: "100 €" })}</option>
              <option value="extended">{t("supportImportPage.scope.extended", { hourlyPrice: "100 €" })}</option>
            </select>
          </div>
          {scope === "extended" && (
            <div>
              <label className="block text-sm mb-1">{t("supportImportPage.estimatedHours")}</label>
              <input className="w-full border rounded px-3 py-2" type="number" min={1} value={hours} onChange={(e) => setHours(e.target.value)} />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm mb-1">{t("supportImportPage.fileLabel")}</label>
          <input type="file" accept=".csv,.xlsx,.doc,.docx,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <p className="text-xs text-gray-500 mt-1">{t("supportImportPage.fileHelp")}</p>
        </div>

        <div>
          <label className="block text-sm mb-1">{t("supportImportPage.notesLabel")}</label>
          <textarea className="w-full border rounded px-3 py-2" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {t("supportImportPage.estimatedAmount")} <b>{t("supportImportPage.amountInclTax", { amount: estimatedAmount })}</b>
          </div>
          <button type="submit" className="btn-artsy btn-primary-artsy" disabled={busy}>
            {busy ? t("supportImportPage.redirecting") : t("supportImportPage.pay")}
          </button>
        </div>

        {error && <div className="text-red-600 text-sm">{error}</div>}
      </form>

      <div className="mt-8 text-sm text-gray-600 space-y-2">
        <p><strong>{t("supportImportPage.details.standardTitle", { price: "100 €" })}</strong> {t("supportImportPage.details.standardDescription")}</p>
        <p><strong>{t("supportImportPage.details.extendedTitle", { hourlyPrice: "100 €" })}</strong> {t("supportImportPage.details.extendedDescription")}</p>
        <p>{t("supportImportPage.afterPayment")}</p>
      </div>
    </div>
  );
}
